import React, { useCallback, useEffect, useState } from "react";
import { platformApi } from "../api";

const errMsg = (e) => e?.response?.data?.message || e?.message || "Request failed";
const ROLES = ["Owner", "Admin", "Support", "Viewer"];
const when = (d) => (d ? new Date(d).toLocaleString() : "—");
const Err = ({ msg }) => (msg ? <div className="err" style={{ marginBottom: 10 }}>{msg}</div> : null);
const Kpi = ({ n, l, cls = "" }) => <div className={`kpi ${cls}`}><div className="n">{n ?? "—"}</div><div className="l">{l}</div></div>;

/* ── Overview ── */
export function Overview() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { platformApi.get("/platform/overview").then((r) => setD(r.data?.data)).catch((e) => setErr(errMsg(e))); }, []);
  if (!d) return <div><h1>Overview</h1><Err msg={err} />{!err && <p className="sub">Loading…</p>}</div>;
  const m = d.machines || {};
  return (
    <div>
      <h1>Overview</h1>
      <p className="sub">Platform-wide counts across all tenants.</p>
      <h3>Tenants</h3>
      <div className="kpis">
        <Kpi n={d.tenants.Active || 0} l="Active tenants" cls="ok" /><Kpi n={d.tenants.Suspended || 0} l="Suspended" cls={d.tenants.Suspended ? "warn" : ""} />
        <Kpi n={d.tenantUsers} l="Tenant users" /><Kpi n={d.platformUsers} l="Platform staff (active)" />
      </div>
      <h3>Agents</h3>
      <div className="kpis">
        <Kpi n={d.agentServers.active} l="Agent Servers (active)" /><Kpi n={d.agentServers.seenLast24h} l="Agent Servers seen in 24 h" />
        <Kpi n={d.agentServers.revoked} l="Agent Servers revoked" />
        <Kpi n={m.Online || 0} l="Machines online" cls="ok" /><Kpi n={m.Idle || 0} l="Idle" /><Kpi n={m.Stale || 0} l="Stale" cls={m.Stale ? "warn" : ""} /><Kpi n={m.Offline || 0} l="Offline" cls={m.Offline ? "danger" : ""} />
        <Kpi n={d.scansLast24h} l="Scans in 24 h" />
      </div>
      <h3>Releases &amp; security</h3>
      <div className="kpis">
        <Kpi n={d.releases.Published || 0} l="Published releases" /><Kpi n={d.releases.Draft || 0} l="Draft releases" />
        <Kpi n={d.loginFailures24h} l="Failed logins in 24 h" cls={d.loginFailures24h > 20 ? "danger" : ""} />
        <Kpi n={d.activeSupportGrants} l="Active support-access records" cls={d.activeSupportGrants ? "warn" : ""} />
      </div>
    </div>
  );
}

/* ── Platform users & roles ── */
export function PlatformUsers() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => platformApi.get("/platform/users").then((r) => setRows(r.data?.data || [])).catch((e) => setErr(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn) => { try { await fn(); setErr(""); load(); } catch (e) { setErr(errMsg(e)); } };
  const create = (e) => { e.preventDefault(); act(async () => { await platformApi.post("/platform/users", form); setForm(null); }); };
  const reset = (u) => { const p = window.prompt(`New password for ${u.username} (min 12 characters):`); if (p) act(() => platformApi.patch(`/platform/users/${u.id}`, { password: p })); };
  return (
    <div>
      <h1>Platform Users &amp; Roles</h1>
      <p className="sub"><b>Owner</b>: everything, including users and settings. <b>Admin</b>: tenants, releases, library. <b>Support</b>: read-only plus support-access records. <b>Viewer</b>: read-only. Only Owners can change this page.</p>
      <Err msg={err} />
      <div className="card">
        <div className="toolbar"><button className="btn primary" onClick={() => setForm(form ? null : { username: "", password: "", role: "Viewer" })}>{form ? "Cancel" : "New user"}</button></div>
        {form && (
          <form onSubmit={create} style={{ padding: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10 }}>
            <div className="form-row"><label>Username</label><input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required /></div>
            <div className="form-row"><label>Password (min 12)</label><input type="password" minLength={12} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required /></div>
            <div className="form-row"><label>Role</label><select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>{ROLES.map((r) => <option key={r}>{r}</option>)}</select></div>
            <div style={{ alignSelf: "end" }}><button className="btn primary">Create</button></div>
          </form>
        )}
        <table>
          <thead><tr><th>Username</th><th>Role</th><th>Status</th><th>Created</th><th /></tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id}>
                <td>{u.username}</td>
                <td><select value={u.role} onChange={(e) => act(() => platformApi.patch(`/platform/users/${u.id}`, { role: e.target.value }))}>{ROLES.map((r) => <option key={r}>{r}</option>)}</select></td>
                <td><span className={`pill ${u.is_active ? "ok" : "danger"}`}>{u.is_active ? "Active" : "Disabled"}</span></td>
                <td>{when(u.created_at)}</td>
                <td>
                  <button className="btn sm" onClick={() => act(() => platformApi.patch(`/platform/users/${u.id}`, { is_active: !u.is_active }))}>{u.is_active ? "Disable" : "Enable"}</button>{" "}
                  <button className="btn sm" onClick={() => reset(u)}>Reset password</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Regulatory library ── */
const TYPES = ["Act", "Rules", "Master Direction", "Circular", "Guidance", "Other"];
const EMPTY_DOC = { title: "", authority: "", doc_type: "Circular", reference: "", version: "", effective_date: "", source_url: "", status: "Current", notes: "" };
export function RegulatoryLibrary() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => platformApi.get("/platform/regulatory").then((r) => setRows(r.data?.data || [])).catch((e) => setErr(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn) => { try { await fn(); setErr(""); load(); } catch (e) { setErr(errMsg(e)); } };
  const save = (e) => { e.preventDefault(); act(async () => { await platformApi.post("/platform/regulatory", form); setForm(null); }); };
  const upload = (d, f) => { if (f) act(() => platformApi.put(`/platform/regulatory/${d.id}/file`, f, { headers: { "Content-Type": "application/pdf" } })); };
  const download = async (d) => {
    try {
      const r = await platformApi.get(`/platform/regulatory/${d.id}/file`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = d.file_name || "document.pdf"; a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { setErr(errMsg(e)); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div>
      <h1>Regulatory Library</h1>
      <p className="sub">The laws, rules and circulars the controls are mapped to. Upload the PDF when you have it; the SHA-256 is recorded so you can prove which version you relied on.</p>
      <Err msg={err} />
      <div className="card">
        <div className="toolbar"><button className="btn primary" onClick={() => setForm(form ? null : { ...EMPTY_DOC })}>{form ? "Cancel" : "Add document"}</button></div>
        {form && (
          <form onSubmit={save} style={{ padding: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10 }}>
            <div className="form-row"><label>Title</label><input value={form.title} onChange={set("title")} required /></div>
            <div className="form-row"><label>Authority</label><input value={form.authority} onChange={set("authority")} placeholder="RBI, MeitY…" required /></div>
            <div className="form-row"><label>Type</label><select value={form.doc_type} onChange={set("doc_type")}>{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
            <div className="form-row"><label>Reference</label><input value={form.reference} onChange={set("reference")} /></div>
            <div className="form-row"><label>Version</label><input value={form.version} onChange={set("version")} /></div>
            <div className="form-row"><label>Effective date</label><input type="date" value={form.effective_date} onChange={set("effective_date")} /></div>
            <div className="form-row"><label>Source URL</label><input value={form.source_url} onChange={set("source_url")} placeholder="https://" /></div>
            <div className="form-row"><label>Notes</label><input value={form.notes} onChange={set("notes")} /></div>
            <div style={{ alignSelf: "end" }}><button className="btn primary">Save</button></div>
          </form>
        )}
        <table>
          <thead><tr><th>Title</th><th>Authority</th><th>Type</th><th>Ref / version</th><th>Status</th><th>File</th></tr></thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id}>
                <td>{d.source_url ? <a href={d.source_url} target="_blank" rel="noopener noreferrer">{d.title}</a> : d.title}{d.notes && <div className="sub" style={{ margin: 0 }}>{d.notes}</div>}</td>
                <td>{d.authority}</td><td>{d.doc_type}</td><td>{[d.reference, d.version].filter(Boolean).join(" · ") || "—"}</td>
                <td><select value={d.status} onChange={(e) => act(() => platformApi.patch(`/platform/regulatory/${d.id}`, { status: e.target.value }))}>{["Current", "Superseded", "Draft"].map((s) => <option key={s}>{s}</option>)}</select></td>
                <td>
                  {d.file_name ? <><button className="btn sm" onClick={() => download(d)}>Download</button> <span title={d.sha256} style={{ fontFamily: "monospace", fontSize: 11 }}>{d.sha256?.slice(0, 10)}…</span></> : <span className="pill warn">Not uploaded</span>}
                  <div><label className="btn link" style={{ cursor: "pointer" }}>{d.file_name ? "Replace PDF" : "Upload PDF"}<input type="file" accept="application/pdf" style={{ display: "none" }} onChange={(e) => { upload(d, e.target.files[0]); e.target.value = ""; }} /></label></div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Audit log & support access ── */
function SupportAccess({ tenants }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ tenant_id: "", hours: 4, reason: "" });
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const load = useCallback(() => platformApi.get("/platform/support-access").then((r) => setRows(r.data?.data || [])).catch((e) => setErr(errMsg(e))), []);
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [load]);
  const create = async (e) => {
    e.preventDefault(); setMsg("");
    try { const r = await platformApi.post("/platform/support-access", { ...form, tenant_id: Number(form.tenant_id), hours: Number(form.hours) }); setErr(""); setMsg(r.data?.message || ""); setForm({ ...form, reason: "" }); load(); } catch (x) { setErr(errMsg(x)); }
  };
  const revoke = async (r) => { try { await platformApi.post(`/platform/support-access/${r.id}/revoke`); load(); } catch (x) { setErr(errMsg(x)); } };
  const open = async (r) => {
    try {
      const s = (await platformApi.post(`/platform/support-access/${r.id}/session`)).data?.data;
      window.open(`${s.url}?token=${encodeURIComponent(s.token)}`, "_blank", "noopener");
      setErr("");
    } catch (x) { setErr(errMsg(x)); }
  };
  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div style={{ padding: 12 }}>
        <b>Support access</b>
        <p className="sub" style={{ margin: "4px 0 10px" }}>Ask a tenant for access. Their administrators see the request in their console and decide. If approved, you can open the tenant <b>read-only</b> for the hours they allow (sessions last up to 30 min and can be reopened). Everything you open is written to the tenant's audit log. Sessions open in a new tab and replace any tenant login you have in this browser.</p>
        {err && <Err msg={err} />}{msg && <div className="ok" style={{ marginBottom: 8 }}>{msg}</div>}
        <form onSubmit={create} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
          <div className="form-row"><label>Tenant</label><select value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })} required><option value="">Select…</option>{tenants.map((t) => <option key={t.id} value={t.id}>{t.slug}</option>)}</select></div>
          <div className="form-row"><label>Hours needed (1-24)</label><input type="number" min="1" max="24" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} required /></div>
          <div className="form-row" style={{ gridColumn: "span 2" }}><label>Reason / ticket (shown to the tenant)</label><input value={form.reason} minLength={10} onChange={(e) => setForm({ ...form, reason: e.target.value })} required /></div>
          <div style={{ alignSelf: "end" }}><button className="btn primary">Request access</button></div>
        </form>
      </div>
      <table>
        <thead><tr><th>Tenant</th><th>By</th><th>Reason</th><th>Hours asked</th><th>State</th><th>Access until</th><th /></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}><td>{r.tenant_slug}</td><td>{r.requested_by}</td><td>{r.reason}</td><td>{r.requested_hours}</td>
              <td><span className={`pill ${r.state === "Approved" ? "warn" : ""}`}>{r.state}</span>{r.decision_note && <div className="sub" style={{ margin: 0 }}>{r.decision_note}</div>}</td>
              <td>{r.state === "Approved" ? when(r.expires_at) : "—"}</td>
              <td>
                {r.state === "Approved" && <button className="btn sm primary" onClick={() => open(r)}>Open tenant (read-only)</button>}{" "}
                {(r.state === "Approved" || r.state === "Pending") && <button className="btn sm danger" onClick={() => revoke(r)}>{r.state === "Pending" ? "Withdraw" : "Revoke"}</button>}
              </td></tr>
          ))}
          {!rows.length && <tr><td colSpan={7} style={{ textAlign: "center", padding: 16 }}>None</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function AuditLog() {
  const [tenants, setTenants] = useState([]);
  const [f, setF] = useState({ tenant_id: "", action: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0 });
  const [err, setErr] = useState("");
  const size = 100;
  useEffect(() => { platformApi.get("/platform/tenants").then((r) => setTenants(r.data?.data || [])).catch(() => {}); }, []);
  const load = useCallback(() => platformApi.get("/platform/audit", { params: { ...f, page, pageSize: size } })
    .then((r) => { setData(r.data?.data || { rows: [], total: 0 }); setErr(""); }).catch((e) => setErr(errMsg(e))), [f, page]);
  useEffect(() => { load(); }, [load]);
  const slug = (id) => (id == null ? "platform" : tenants.find((t) => t.id === id)?.slug || id);
  const pages = Math.max(1, Math.ceil(data.total / size));
  return (
    <div>
      <h1>Audit Log &amp; Support Access</h1>
      <SupportAccess tenants={tenants} />
      <h3>Audit log</h3>
      <Err msg={err} />
      <div className="card">
        <div className="toolbar" style={{ gap: 8 }}>
          <select value={f.tenant_id} onChange={(e) => { setPage(1); setF({ ...f, tenant_id: e.target.value }); }}><option value="">All tenants</option>{tenants.map((t) => <option key={t.id} value={t.id}>{t.slug}</option>)}</select>
          <input placeholder="Action starts with… (e.g. login)" value={f.action} onChange={(e) => { setPage(1); setF({ ...f, action: e.target.value }); }} />
          <span className="sub" style={{ margin: 0 }}>{data.total} events</span>
        </div>
        <table>
          <thead><tr><th>When</th><th>Scope</th><th>Actor</th><th>Action</th><th>Target</th><th>IP</th><th>Detail</th></tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.id}><td>{when(r.at)}</td><td>{slug(r.tenant_id)}</td><td>{r.actor || "—"}</td><td><b>{r.action}</b></td><td>{r.target}</td><td>{r.ip}</td>
                <td style={{ fontFamily: "monospace", fontSize: 11 }}>{r.detail ? JSON.stringify(r.detail) : ""}</td></tr>
            ))}
            {!data.rows.length && <tr><td colSpan={7} style={{ textAlign: "center", padding: 16 }}>No events</td></tr>}
          </tbody>
        </table>
        <div className="toolbar"><button className="btn sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button> Page {page} / {pages} <button className="btn sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button></div>
      </div>
    </div>
  );
}

/* ── Settings ── */
export function PlatformSettings() {
  const [d, setD] = useState(null);
  const [v, setV] = useState({});
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => { platformApi.get("/platform/settings").then((r) => { setD(r.data?.data); setV(r.data?.data?.values || {}); }).catch((e) => setErr(errMsg(e))); }, []);
  const save = async (e) => {
    e.preventDefault(); setMsg(""); setErr("");
    try { await platformApi.put("/platform/settings", v); setMsg("Saved."); } catch (x) { setErr(errMsg(x)); }
  };
  if (!d) return <div><h1>Settings</h1><Err msg={err} /></div>;
  const yn = (b) => <span className={`pill ${b ? "ok" : "warn"}`}>{b ? "Yes" : "No"}</span>;
  return (
    <div>
      <h1>Settings</h1>
      <Err msg={err} />{msg && <div className="ok" style={{ marginBottom: 10 }}>{msg}</div>}
      <form onSubmit={save} className="card" style={{ padding: 14, display: "grid", gap: 12, maxWidth: 640 }}>
        <div className="form-row"><label>Default plan for new tenants</label><input maxLength={40} value={v.default_plan || ""} onChange={(e) => setV({ ...v, default_plan: e.target.value })} /><small>{d.hints.default_plan}</small></div>
        <div className="form-row"><label><input type="checkbox" checked={!!v.allow_new_tenants} onChange={(e) => setV({ ...v, allow_new_tenants: e.target.checked })} /> Allow creating new tenants</label><small>{d.hints.allow_new_tenants}</small></div>
        <div className="form-row"><label>Console notice</label><input maxLength={300} value={v.console_notice || ""} onChange={(e) => setV({ ...v, console_notice: e.target.value })} /><small>{d.hints.console_notice}</small></div>
        <div><button className="btn primary">Save settings</button> <span className="sub">Owners only.</span></div>
      </form>
      <h3 style={{ marginTop: 20 }}>Server configuration (read-only, set in the server environment)</h3>
      <div className="card"><table><tbody>
        <tr><td>Release signing public key configured</td><td>{yn(d.env.releaseSigningKeyConfigured)}</td></tr>
        <tr><td>Dedicated DPDP JWT secret</td><td>{yn(d.env.dedicatedJwtSecret)}</td></tr>
        <tr><td>Legacy PII ingest enabled (should be No)</td><td>{yn(!d.env.legacyPiiIngest)}</td></tr>
        <tr><td>Bootstrap "everyone is admin" (should be No)</td><td>{yn(!d.env.bootstrapAdmin)}</td></tr>
        <tr><td>Platform session length</td><td>{d.env.platformTokenTtl}</td></tr>
      </tbody></table></div>
    </div>
  );
}
